import { useEventCallback } from '@mui/material/utils';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';

import { useDataMutation, useDeleteEntity, useUser } from '@work4all/data';

import { UserPresenceTimeInfoItem } from '@work4all/models/lib/Classes/UserPresenceTimeInfoItem.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { TimeStampKind } from '@work4all/models/lib/Enums/TimeStampKind.enum';

import { useForceUpdate } from '@work4all/utils/lib/hooks/use-force-update';

import { useTimeTracker } from '../../../../time-tracker/use-time-tracker';
import {
  checkIsBeforeEarliestCheckinTime,
  getEearliestCheckinTime,
} from '../../../../time-tracker/utils/checkIsBeforeEarliestCheckingTime';

import { useTimeCardDetails } from './useTimeCardDetails';

const areDayContextEqual = (newDateTime: DateTime, oldDateTime: DateTime) => {
  return (
    newDateTime.hasSame(oldDateTime, 'minute') &&
    newDateTime.hasSame(oldDateTime, 'hour')
  );
};

const DEFAULT_MINUTES_TO_ADD = 1;

export const useTimeEntryMutation = () => {
  const { date, items, user, refetch } = useTimeCardDetails();

  const currentUser = useUser();
  const isCurrentUser = user.id === currentUser.benutzerCode;

  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();
  const dialogs = useDialogs();

  const responseData = useMemo(() => {
    const data: UserPresenceTimeInfoItem<EMode.query> = {
      id: null,
    };
    return data;
  }, []);

  const [deleteEntity] = useDeleteEntity(false);

  const [mutate] = useDataMutation<UserPresenceTimeInfoItem, EMode.upsert>({
    entity: Entities.userPresenceTimeInfoItem,
    mutationType: EMode.upsert,
    responseData,
    resetStore: false,
    onCompleted: refetch,
  });

  const lastEntryValid = useRef(true);
  const lastEntry = useRef<{ id: number; timestamp: string }>(null);

  const save = useEventCallback((id: number, timestamp: string) => {
    const dateTime = DateTime.fromISO(timestamp);
    lastEntryValid.current = validate(id, dateTime);
    if (lastEntryValid.current) {
      lastEntry.current = {
        id,
        timestamp,
      };
    }
  });

  const earliestCheckinTime = useMemo(
    () => getEearliestCheckinTime(user.kommtGehtMinimaleStartZeit),
    [user.kommtGehtMinimaleStartZeit]
  );

  const tracker = useTimeTracker({
    amplitudeEntryPoint: 'TimeCard',
  });
  const { start, pause, resume, stop } = tracker ?? {};

  const getLast = useCallback(() => {
    const filtered = items
      .flatMap((x) => x.items)
      .filter((x) =>
        DateTime.fromISO(x.timestamp).hasSame(DateTime.fromISO(date), 'day')
      );
    const last = filtered[filtered.length - 1];
    return last ? DateTime.fromISO(last.timestamp) : undefined;
  }, [items, date]);

  const guardValidTime = useCallback(
    (current: DateTime, isEndOfPause = false): DateTime => {
      const numberOfMinutesToAdd = isEndOfPause
        ? user.workTimeTrackingMinBreakTime || DEFAULT_MINUTES_TO_ADD
        : DEFAULT_MINUTES_TO_ADD;

      return current.plus({ minute: numberOfMinutesToAdd });
    },
    [user.workTimeTrackingMinBreakTime]
  );

  const getLastSafe = useCallback(
    (isEndOfPause = false) => {
      const last = getLast();
      return last ? guardValidTime(last, isEndOfPause) : undefined;
    },
    [getLast, guardValidTime]
  );

  const validateStartTime = useCallback(
    (date: DateTime): boolean => {
      const isBeforeEarliestCheckinTime = checkIsBeforeEarliestCheckinTime(
        earliestCheckinTime,
        date
      );

      if (isBeforeEarliestCheckinTime) {
        enqueueSnackbar(
          t('ERROR.TimeTracking_earliestStartTime', {
            earliestStartTime: earliestCheckinTime?.toLocaleString(
              DateTime.TIME_SIMPLE
            ),
          }),
          { variant: 'error' }
        );
        return false;
      }

      return true;
    },
    [enqueueSnackbar, t, earliestCheckinTime]
  );

  const validate = useCallback(
    (id: number, currentTime: DateTime) => {
      const flatmap = items.flatMap((x) => x.items);
      const currentIdx = flatmap.findIndex((x) => x.id === id);

      const prev = flatmap[currentIdx - 1];
      const next = flatmap[currentIdx + 1];

      const isBiggerThenPrev = prev
        ? DateTime.fromISO(prev.timestamp) < currentTime
        : true;
      const isLessThenNext = next
        ? DateTime.fromISO(next.timestamp) > currentTime
        : true;

      return isBiggerThenPrev && isLessThenNext;
    },
    [items]
  );

  const overflowInDateContext = useCallback(
    (newTime: DateTime, extendedContext: DateTime[] = []) => {
      const endOfDay = DateTime.fromISO(date).endOf('day');
      const map = items
        .flatMap((x) => x.items)
        .map((x) => DateTime.fromISO(x.timestamp));
      if (newTime >= endOfDay) {
        const alreadyEnd = [...map, ...extendedContext].some(
          (x) => x.toFormat('HH:mm') === endOfDay.toFormat('HH:mm')
        );
        if (alreadyEnd) return undefined;
        return endOfDay;
      }

      const sorted = [...map, ...extendedContext, newTime].sort((a, b) =>
        a < b ? 1 : -1
      );
      const timestamp = DateTime.fromISO(date).set({
        hour: sorted[0].hour,
        minute: sorted[0].minute,
      });

      return timestamp;
    },
    [date, items]
  );

  const forceUpdate = useForceUpdate();

  return {
    add: useEventCallback(async () => {
      const last = getLastSafe();
      const currentDate = DateTime.fromISO(date);

      let timestamp: DateTime | undefined = undefined;

      const now = DateTime.now();
      const isToday = currentDate.hasSame(now, 'day');
      const isBeforeEarliestCheckinTime = checkIsBeforeEarliestCheckinTime(
        earliestCheckinTime,
        currentDate
      );

      if (last) {
        const overflow = overflowInDateContext(last);
        if (!overflow) return;
        timestamp = currentDate.set({
          hour: overflow.hour,
          minute: overflow.minute,
        });
      } else if (isToday && isBeforeEarliestCheckinTime) {
        if (isCurrentUser) {
          await dialogs.alert({
            title: t('TIME_TRACKER.EARLIEST_CHECKIN_TITLE'),
            description: t('TIME_TRACKER.EARLIEST_CHECKIN_MESSAGE', {
              time: earliestCheckinTime?.toLocaleString(DateTime.TIME_SIMPLE),
            }),
            closeLabel: t('COMMON.OK'),
            onClose: () => {
              timestamp = earliestCheckinTime;
            },
          });
        } else {
          timestamp = DateTime.now();
        }
      } else {
        const baseTime = currentDate.set({
          hour: 9,
          minute: 0,
          second: 0,
          millisecond: 0,
        });
        const userEarliestCheckinTime = getEearliestCheckinTime(
          user.kommtGehtMinimaleStartZeit,
          currentDate
        );
        const isBeforeEarliestCheckinTime = checkIsBeforeEarliestCheckinTime(
          userEarliestCheckinTime,
          baseTime
        );

        timestamp = isBeforeEarliestCheckinTime
          ? userEarliestCheckinTime
          : baseTime;
      }

      try {
        await start(timestamp.toJSDate(), user.id);
      } finally {
        refetch();
      }
    }),
    pause: useEventCallback(async () => {
      const last = getLastSafe();
      const pauseTime = overflowInDateContext(last);
      if (!pauseTime) return;

      try {
        await pause(pauseTime.toJSDate(), user.id);
      } finally {
        refetch();
      }
    }),
    resume: useEventCallback(async () => {
      const last = getLastSafe(true);
      const resumeTime = overflowInDateContext(last);
      if (!resumeTime) return;

      try {
        await resume(resumeTime.toJSDate(), user.id);
      } finally {
        refetch();
      }
    }),

    stop: useEventCallback(async () => {
      const allStartsToday = items
        .flatMap((x) => x.items)
        .filter((x) =>
          DateTime.fromISO(x.timestamp).hasSame(DateTime.fromISO(date), 'day')
        )
        .filter((x) => x.kind === TimeStampKind.MANUELL);

      const singleStart = allStartsToday.length === 1;
      const newEnd = singleStart
        ? DateTime.fromISO(allStartsToday[0].timestamp).plus({ hours: 8 })
        : undefined;

      const last = getLastSafe();

      const now = DateTime.now();
      const isToday = DateTime.fromISO(allStartsToday[0].timestamp).hasSame(
        now,
        'day'
      );
      const isBeforeEarliestCheckinTime =
        checkIsBeforeEarliestCheckinTime(earliestCheckinTime);

      const timestamp =
        isToday && isBeforeEarliestCheckinTime
          ? DateTime.now()
          : overflowInDateContext(last, [newEnd]);
      if (!timestamp) return;

      try {
        await stop(timestamp.toJSDate(), user.id);
      } finally {
        refetch();
      }
    }),
    update: useEventCallback((id: number, value: string) => {
      const item = items.flatMap((x) => x.items).find((x) => x.id === id);

      const newDateTime = DateTime.fromISO(value);
      const oldDateTime = DateTime.fromISO(item.timestamp);

      if (!areDayContextEqual(newDateTime, oldDateTime)) {
        save(id, value);
      }
    }),
    remove: useEventCallback(async (id: number) => {
      try {
        await deleteEntity({
          ids: [id.toString()],
          type: Entities.userPresenceTimeInfoItem,
        });
        const map = items.flatMap((x) => x.items);
        const itemIdx = map.findIndex((x) => x.id === id);
        const item = map[itemIdx];
        if (item.kind === TimeStampKind.PAUSENENDE) {
          const pauseStart = map[itemIdx - 1];
          if (pauseStart.kind === TimeStampKind.PAUSENBEGINN)
            await deleteEntity({
              ids: [pauseStart.id.toString()],
              type: Entities.userPresenceTimeInfoItem,
            });
        }
      } finally {
        refetch();
      }
    }),
    onBlur: useEventCallback(() => {
      if (lastEntry.current) {
        const { id, timestamp } = lastEntry.current;

        const flatmap = items.flatMap((x) => x.items);
        const currentIdx = flatmap.findIndex((x) => x.id === id);
        const prevTimeStamp = flatmap[currentIdx]?.timestamp;
        const isStartTimeValid = prevTimeStamp
          ? validateStartTime(DateTime.fromISO(timestamp))
          : true;

        if (isStartTimeValid) mutate(lastEntry.current);
        else {
          forceUpdate();
        }
      }
    }),
  };
};
